"use client";

/**
 * Per-comment three-dot overflow menu on /markets/[slug]. Mirrors
 * apps/web/components/posts/PostCommentOverflowMenu.tsx, swapping
 * the report modal target from post-comment to market-comment.
 *
 * Actions:
 *   - Copy link → /markets/{slug}#comment-{commentId}
 *   - Mute author → "Soon" placeholder (no backend yet)
 *   - Report → opens MarketCommentFlagModal (replaces the prior
 *              CommentFlagButton's inline expanding form, which
 *              fell short of the social-media bar)
 *
 * Hidden when viewer is the author; Report row hidden when unauth.
 *
 * 2026-05-06 — copied from PostCommentOverflowMenu to ship parity
 * faster. Filed for follow-up dedup into a generic
 * CommentOverflowMenu that takes onReport / copyLinkUrl as props.
 */

import { useEffect, useRef, useState } from "react";
import {
  Flag,
  Link as LinkIcon,
  MoreHorizontal,
  VolumeX,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { MarketCommentFlagModal } from "./MarketCommentFlagModal";

interface Props {
  marketSlug: string;
  commentId: string;
  commentExcerpt?: string | null;
  isOwnComment?: boolean;
  authed?: boolean;
}

export function MarketCommentOverflowMenu({
  marketSlug,
  commentId,
  commentExcerpt,
  isOwnComment = false,
  authed = false,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!feedback) return;
    const id = window.setTimeout(() => setFeedback(null), 2400);
    return () => window.clearTimeout(id);
  }, [feedback]);

  if (isOwnComment) return null;

  async function handleCopyLink() {
    setOpen(false);
    try {
      const url = `${window.location.origin}/markets/${marketSlug}#comment-${commentId}`;
      await navigator.clipboard.writeText(url);
      setFeedback(t("marketCommentOverflow.copied", "Link copied"));
    } catch {
      setFeedback(t("marketCommentOverflow.copyFailed", "Couldn't copy"));
    }
  }

  function handleReport() {
    setOpen(false);
    setFlagOpen(true);
  }

  return (
    <div ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("marketCommentOverflow.aria", "More actions")}
        onClick={() => setOpen((v) => !v)}
        className="group inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--color-text3)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
      >
        <MoreHorizontal
          size={14}
          strokeWidth={2}
          aria-hidden="true"
          className="transition-transform group-active:scale-90"
        />
      </button>

      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-full z-30 mt-1 min-w-[200px] overflow-hidden rounded-md border bg-[var(--color-surface)] shadow-lg"
          style={{ borderColor: "var(--color-border)" }}
        >
          <li>
            <button
              type="button"
              role="menuitem"
              onClick={handleCopyLink}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-[var(--color-text)] transition-colors hover:bg-[var(--color-bg)]"
            >
              <LinkIcon size={14} strokeWidth={1.75} aria-hidden="true" />
              {t("marketCommentOverflow.copyLink", "Copy link")}
            </button>
          </li>

          <li>
            <button
              type="button"
              role="menuitem"
              disabled
              title={t(
                "marketCommentOverflow.muteSoon",
                "Mute author — coming soon",
              )}
              className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-left text-[13px] text-[var(--color-text3)]"
            >
              <VolumeX size={14} strokeWidth={1.75} aria-hidden="true" />
              {t("marketCommentOverflow.muteAuthor", "Mute author")}
              <span className="ml-auto text-[10px] uppercase tracking-[0.4px]">
                {t("marketCommentOverflow.soon", "Soon")}
              </span>
            </button>
          </li>

          {authed && (
            <li>
              <button
                type="button"
                role="menuitem"
                onClick={handleReport}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-[var(--color-bg)]"
                style={{ color: "var(--color-no)" }}
              >
                <Flag size={14} strokeWidth={1.75} aria-hidden="true" />
                {t("marketCommentOverflow.report", "Report comment")}
              </button>
            </li>
          )}
        </ul>
      )}

      {feedback && (
        <span
          role="status"
          className="absolute right-0 top-full z-20 mt-1 whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-bold"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
          }}
        >
          {feedback}
        </span>
      )}

      <MarketCommentFlagModal
        open={flagOpen}
        onClose={() => setFlagOpen(false)}
        commentId={commentId}
        commentExcerpt={commentExcerpt ?? null}
      />
    </div>
  );
}
